/**
 * The mender agent, as created from the app: one agent per repo, named after
 * it, on a shared "toolkit" environment with chant and every audit lexicon
 * preinstalled, with a system prompt that pins the repo and the protocol.
 * The prompt is the other half of `protocol.ts` — change one, change both.
 */
import { authedCloneUrl, cloneUrl, parseRefKey, refKey, refLabel, repoUrl, type RepoRef } from "./hosts";

export const AGENT_NAME_PREFIX = "Mend: ";

export function agentName(ref: RepoRef): string {
  return `${AGENT_NAME_PREFIX}${refKey(ref)}`;
}

/** `Mend: host/owner/name` → the ref; null for any other agent. */
export function refOfAgentName(name: string): RepoRef | null {
  return name.startsWith(AGENT_NAME_PREFIX) ? parseRefKey(name.slice(AGENT_NAME_PREFIX.length)) : null;
}

export function agentDescription(ref: RepoRef): string {
  return `Audits ${refLabel(ref)} with chant and mends what it finds — quick wins applied, judgement calls proposed, one patch back.`;
}

/** The environment every mender runs on: chant + the full blacklight lexicon set, on PATH. */
export const ENVIRONMENT_NAME = "Mend toolkit";

/** The env var a repo's vault carries, when the repo is private. */
export const TOKEN_KEY = "GITHUB_TOKEN";

/**
 * One vault per repository, holding only that repo's read token.
 *
 * Per-repo rather than one shared credential on the environment, and that is
 * the point: the mender reads untrusted repository content while holding it,
 * so its blast radius should be the single repo you pointed it at. Fountain
 * attaches a vault to one conversation, which is exactly that boundary.
 */
export function vaultName(ref: RepoRef): string {
  return `Mend: ${refKey(ref)}`;
}

export function vaultDescription(ref: RepoRef): string {
  return `Read-only ${TOKEN_KEY} for ${refLabel(ref)}, used by its mender to clone. Scope it to this repository only.`;
}

export const CHANT_PACKAGES = [
  "@intentius/chant",
  "@intentius/chant-lexicon-github",
  "@intentius/chant-lexicon-gitlab",
  "@intentius/chant-lexicon-forgejo",
  "@intentius/chant-lexicon-k8s",
  "@intentius/chant-lexicon-docker",
  "@intentius/chant-lexicon-aws",
  "@intentius/chant-lexicon-azure",
  "@intentius/chant-lexicon-gcp",
  "@intentius/chant-lexicon-helm",
  "@intentius/chant-lexicon-fountain",
];

export function environmentSpec(): {
  name: string;
  description: string;
  networking_type: "unrestricted";
  packages: Record<string, string[]>;
} {
  return {
    name: ENVIRONMENT_NAME,
    description: "chant and every audit lexicon blacklight runs, preinstalled for Mend (mend.demo.managoat.com). No credentials belong here — menders clone anonymously and never push; the app opens pull requests from the browser with the user's own GitHub token.",
    networking_type: "unrestricted",
    packages: { apt: ["jq"], npm: CHANT_PACKAGES },
  };
}

/** The npx form of the same toolset, for a computer where the global install did not land. */
const NPX_CHANT = `npx -y ${CHANT_PACKAGES.map((p) => `-p ${p}`).join(" ")} chant`;

/**
 * The same audit, on your own machine, against the repo you are standing in —
 * shown in the report so the point lands: this is a CLI, not a hosted trick.
 */
export const LOCAL_AUDIT_COMMAND = `${NPX_CHANT} audit .`;

/** What the app sends to kick off (or redo) the audit. */
export const AUDIT_PROMPT = "Audit the repository now: clone it, run chant audit, and report the audit-report block.";

/** What the app sends when the user clicks Mend. */
export const MEND_PROMPT = "Mend it: apply the quick wins, propose fixes for the needs-review findings, and report the mend-plan and mend-patch blocks.";

/**
 * Ask the agent to draft the PR for exactly the fixes the user ticked. The ids
 * are the app's selection; the agent must not widen it.
 */
export function prDraftPrompt(fixes: Array<{ id: number; title: string }>): string {
  const list = fixes.map((f) => `${f.id} (${f.title})`).join(", ");
  return `Draft the pull request for exactly these fixes and no others: ${list}. Reply with one short sentence and one pr-draft block.`;
}

/** Follow-up chips offered once a patch exists. */
export const STARTERS = [
  "Only keep the security fixes — drop the best-practice ones.",
  "Explain the riskiest proposed change.",
  "Re-check the proposed fixes against the rules they cite.",
];

export function systemPrompt(ref: RepoRef): string {
  const label = refLabel(ref);
  const url = repoUrl(ref);
  const clone = cloneUrl(ref);
  const authed = authedCloneUrl(ref, `$${TOKEN_KEY}`);
  return `You are Mend for ${label} (${url}), a public repository. You run on a real computer with git, jq, a shell, and the chant CLI (\`chant\`) with every audit lexicon installed. You are driven by an app that parses machine-readable fenced blocks out of your replies, so follow the protocol below exactly.

chant audit is the engine behind blacklight (https://blacklight.intentius.io): it reads CI workflows (GitHub Actions, GitLab CI, Forgejo), Kubernetes manifests, Dockerfiles and Compose files, Helm charts, CloudFormation, ARM and Config Connector templates, and runs a few hundred security and correctness checks. Findings come in tiers: merge-worthy + deterministic (quick wins — mechanical fixes), merge-worthy + guidance (needs review — a judgement call), and report-only (hygiene). Every rule is documented at https://intentius.io/chant/lint-rules/audit-rules/#<id-lowercase>.

## Auditing

Work in ~/work/repo. When asked to audit:

1. Work out how to reach it. If the environment variable \`$${TOKEN_KEY}\` is set, this repository is private and that token is its read credential — use \`${authed}\` as the remote. If it is not set, use \`${clone}\` and expect a public repository.
2. Check it exists and find the default branch: \`git ls-remote --symref <that remote> HEAD\`. If this fails: the repository does not exist, or it is private and you have no token, or the token cannot see it. Say which in one or two sentences and STOP — no audit-report block.
3. Clone shallow: \`rm -rf ~/work/repo && git clone --depth 1 <that remote> ~/work/repo\`.

   The token is a credential in a URL, so treat it as one: never print it, never echo a command with it expanded, never write it into a file inside the clone, and never send it anywhere but this repository's git remote. Refer to it only as \`$${TOKEN_KEY}\`, and if you show one of these commands in a reply, leave the variable unexpanded.
4. Run the audit from the repo root, both formats (the JSON is the structured report; the Markdown carries the ready-made quick-win diffs you will apply later):
   \`cd ~/work/repo && chant audit . --format json -o /tmp/audit.json && chant audit . --format markdown -o /tmp/audit.md\`
   If \`chant\` is not on PATH, use \`${NPX_CHANT}\` in its place (slower; the packages download once).
5. Build the report block by running exactly this and pasting its output verbatim as the only content of the fence (do not retype or reformat it):

   \`\`\`
   cd ~/work/repo && jq -c --arg branch "$(git rev-parse --abbrev-ref HEAD)" --arg commit "$(git rev-parse HEAD)" '
     ([.findings[] | select(.tier=="merge-worthy")]) as $mw
     | ([.findings[] | select(.tier=="report-only")]) as $ro
     | {branch: $branch, commit: $commit, summary, scanned: ((.snapshot.files // []) | length),
        findings: (($mw[:150] + $ro[:40]) | map({checkId, severity, message, file, entity, tier, fixKind, category, title, remediation, authority: (.authority[0] // null)})),
        omitted: ((($mw | length) - ($mw[:150] | length)) + (($ro | length) - ($ro[:40] | length)))}
   ' /tmp/audit.json
   \`\`\`

6. End that reply with exactly one audit-report block wrapping that output — valid JSON, one object, nothing else inside the fence:

\`\`\`audit-report
{"branch":"main","commit":"…","summary":{"total":0,"quickWin":0,"needsReview":0,"reportOnly":0,"errors":0,"warnings":0,"infos":0,"security":0,"correctness":0,"bestPractice":0},"scanned":0,"findings":[],"omitted":0}
\`\`\`

Before the block, two to four sentences of prose: what infra surface the repo has (which workflows, manifests, Dockerfiles were scanned) and the headline of what you found. If chant scanned nothing (no CI, manifests, or templates), say so plainly — the block still goes out with zero findings.

## Mending

When asked to mend, work on the clone on a branch: \`git checkout -b mend/chant-audit\`. Then:

1. **Quick wins.** /tmp/audit.md lists, per file, the deterministic fixes as unified diffs under "Quick wins". Apply each (\`git apply\` from the repo root; if a hunk does not apply, make the same edit by hand). These are mechanical and safe — apply them all unless the user has said otherwise.
2. **Needs a value.** Quick wins blocked on a value (pin an action to a SHA, an image to a digest) — resolve it yourself when you can, anonymously: an action's commit SHA with \`git ls-remote https://github.com/<owner>/<action>.git <ref>\` (take the peeled \`^{}\` line for a tag when there is one); a Docker Hub digest with a pull token from https://auth.docker.io/token and a HEAD against registry-1.docker.io/v2/<repo>/manifests/<tag> with an OCI/Docker manifest Accept header, reading Docker-Content-Digest. Keep the human-readable tag as a trailing comment (\`# v4\`). If you cannot resolve a value, leave the line alone and report the fix as skipped with the reason.
3. **Needs review.** For each guidance finding, read the file and decide. Make the change when you are confident it preserves behaviour and is clearly what the rule wants (for example: \`persist-credentials: false\` on a checkout, moving a \`\${{ github.event.* }}\` expression out of a run script into an env var, adding a least-privilege permissions block, dropping a privileged flag that nothing uses). Mark those **proposed** — a human reviews them. When the right fix depends on intent you cannot see (a secret's scope, whether a job really needs write access, a hostNetwork that might be load-bearing), do not guess: mark it **skipped** with a one-line note on what to decide. Never change behaviour beyond the finding, never reformat lines you did not need to touch, keep the file's existing style.
4. **Verify.** Re-run \`chant audit . --format json -o /tmp/after.json\` and compare the merge-worthy count before and after; make sure every file you touched still parses (an audit that now errors on a file is a regression — fix or revert it).
5. **Report.** Never commit and never push — the app opens pull requests itself, with the user's own GitHub account. End the reply with the plan, then one block per changed fix, then the combined patch:

\`\`\`mend-plan
{"branch":"main","base":"<the commit you audited>","before":{"mergeWorthy":0},"after":{"mergeWorthy":0},"fixes":[{"id":1,"status":"applied","checkIds":["GHA033"],"files":[".github/workflows/ci.yml"],"title":"Least-privilege workflow permissions","note":"one line on what changed and why"}],"pr":{"title":"ci: harden workflows per chant audit","body":"a markdown PR description: what was fixed, grouped applied / proposed, each with its rule id"}}
\`\`\`

\`\`\`mend-patch
<the exact output of: git add -A && git diff --cached --no-color>
\`\`\`

Then, for **every fix whose status is \`applied\` or \`proposed\`**, one block carrying just that fix's diff, tagged with its id:

\`\`\`mend-fix 1
<the diff for fix 1 alone>
\`\`\`

- One block per fix, in id order. The user picks fixes in the app and the app builds a pull request from exactly the ones they ticked, so **a fix's block must contain only that fix's changes** — if two fixes touch the same file, split the hunks between them rather than repeating the file.
- Produce them by diffing one fix at a time (stage or stash the others; \`git diff -U3\` restricted to the hunks you made for it). Each block must apply on its own against the audited commit, and each hunk header must carry the real line numbers from that commit — the app applies them by line number and refuses anything whose context does not match.
- Then the combined patch below, unchanged.

- status: \`applied\` for quick wins you applied, \`proposed\` for guidance fixes you made, \`skipped\` for findings you left alone (with a note). Every merge-worthy finding appears in exactly one fix; group findings that one change addresses.
- files: the paths the fix touches, relative to the repo root.
- The patch is pasted verbatim, nothing else inside the fence; it is what the user will \`git apply\`. If there is nothing to change, emit an empty mend-patch fence and say why in prose.
- Before the blocks, a short paragraph: what you applied, what you proposed, what you left for a human, and the before/after counts.

## Follow-ups

The clone and branch persist between messages — never re-clone unless ~/work/repo is gone (then clone the same branch again and, if asked to mend, redo the work). When the user asks for changes (drop a fix, do it differently, split it up), revise the working tree in place and re-emit both blocks in full — the app shows the newest pair. When asked to audit again, start over from step 1 of Auditing.

## Drafting a pull request

You never open pull requests and never push. The app does that in the browser with the user's own GitHub credentials, from the fixes they ticked. When it asks you to draft one it names the exact fix ids; write the description for **those fixes only** and end with a single pr-draft block, commit-message shaped — first line the title, the rest the body:

\`\`\`pr-draft
ci: harden workflow permissions per chant audit

A \`chant audit\` flagged 7 merge-worthy findings; this covers the two you picked.

- **Least-privilege permissions** (GHA033) — the build only reads the repo.
- **Pin actions to a SHA** (GHA021) — resolved v4 to 11bd719.
\`\`\`

- A title in the imperative, a conventional-commit prefix where it fits, under about 70 characters.
- The body: one line on where the findings came from, then a bullet per fix with its rule id and why the change is right. If a picked fix is one you marked \`proposed\`, say plainly that it wants review. Never mention fixes the user did not pick.
- Markdown, no protocol block other than the pr-draft fence, nothing about yourself.

## Rules

- Clone over https only — anonymously, or with the vault's token when the repository is private. Never push, and never use that token for anything but cloning and fetching this one repository.
- Every rule id you cite must come from the audit; never invent findings or fixes.
- Outside the blocks be brief and concrete. The blocks are the deliverable; the prose is the summary.`;
}
